import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Package } from "lucide-react";
import { api, money, resolveImg, track } from "../lib/api";
import { AddKitButton } from "../components/AddKitButton";

const LEVEL_LABEL = { basic: "Basic — Essentials", complete: "Complete — Most Popular", advanced: "Advanced — Maximum Readiness" };

const KitCard = ({ kit }) => {
  const isComplete = kit.level === "complete";
  return (
    <div data-testid={`kits-page-card-${kit.slug}`}
      className={`bg-white flex flex-col ${isComplete ? "border-2 border-[#4A5D23] relative" : "border border-black/10"}`}>
      {isComplete && (
        <span data-testid={`most-popular-badge-${kit.slug}`} className="absolute -top-3 left-5 z-10 text-[10px] font-bold uppercase tracking-widest bg-[#4A5D23] text-white px-3 py-1">Most Popular</span>
      )}
      <Link to={`/kit/${kit.slug}`} onClick={() => track("kit_click", "kits_page", { kit: kit.slug, level: kit.level })}
        className="relative block aspect-[16/9] overflow-hidden bg-[#F4F3F0]">
        <img src={resolveImg(kit.image)} alt={kit.name} loading="lazy" className="w-full h-full object-cover" />
        {!kit.available && <span className="absolute top-2 right-2 text-[10px] font-bold uppercase tracking-widest bg-[#8B0000] text-white px-2 py-1">Temporarily Unavailable</span>}
      </Link>
      <div className="p-5 flex-1 flex flex-col">
        <p className={`text-[10px] font-bold uppercase tracking-[0.2em] ${isComplete ? "text-[#4A5D23]" : "text-black/40"}`}>{LEVEL_LABEL[kit.level] || kit.level}</p>
        <h3 className="font-heading font-bold uppercase tracking-tight text-lg mt-1">{kit.name}</h3>
        <p className="text-xs text-black/50 mt-1 flex-1">{kit.description}</p>
        <div className="flex items-center gap-2 mt-3 text-[11px] text-black/50">
          <Package size={13} className="text-[#4A5D23]" /> {kit.item_count} products included
        </div>
        <div className="flex -space-x-1.5 mt-2">
          {(kit.products || []).slice(0, 6).map((p) => (
            <img key={p.slug} src={resolveImg(p.images?.[0])} alt={p.name} loading="lazy" className="w-8 h-8 object-cover bg-[#F4F3F0] border border-white shrink-0" />
          ))}
        </div>
        <div className="mt-4 flex items-baseline gap-2 flex-wrap">
          <span className="font-heading font-black text-xl">{money(kit.bundle_price)}</span>
          <span className="text-xs text-black/40 line-through">{money(kit.retail_value)}</span>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#4A5D23]">Save {money(kit.savings)} ({kit.savings_pct}%)</span>
        </div>
        <Link data-testid={`view-kit-${kit.slug}`} to={`/kit/${kit.slug}`} onClick={() => track("kit_click", "kits_page", { kit: kit.slug, level: kit.level })}
          className={`mt-4 block text-center py-3 text-xs font-bold uppercase tracking-widest transition-colors duration-150 ${isComplete ? "bg-[#4A5D23] text-white hover:bg-[#3D4D1D]" : "bg-[#121212] text-white hover:bg-[#4A5D23]"}`}>
          View Kit
        </Link>
        {kit.available ? (
          <AddKitButton kit={kit} label="Add to Cart" testId={`kits-page-add-${kit.slug}`}
            className="mt-2 w-full border border-black/20 py-3 text-xs font-bold uppercase tracking-widest hover:border-[#4A5D23] hover:text-[#4A5D23] transition-colors duration-150" />
        ) : (
          <button disabled className="mt-2 w-full border border-black/10 py-3 text-xs font-bold uppercase tracking-widest text-black/30">Unavailable</button>
        )}
      </div>
    </div>
  );
};

export default function Kits() {
  const [kits, setKits] = useState([]);
  useEffect(() => {
    api.get("/kits").then(({ data }) => {
      setKits(data);
      track("kit_impression", "kits_page", { count: data.length });
    }).catch(() => {});
  }, []);

  const families = [];
  kits.forEach((k) => {
    let f = families.find((x) => x.key === k.family);
    if (!f) { f = { key: k.family || "other", name: k.family_name || "Ready Kits", kits: [] }; families.push(f); }
    f.kits.push(k);
  });

  return (
    <div>
      <section className="bg-[#121212] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#8FA84E] font-mono">Shodan Ready Kits</p>
          <h1 className="font-heading text-4xl sm:text-5xl uppercase font-black tracking-tighter mt-2">Stop Guessing.<br />Start Preparing.</h1>
          <p className="text-white/70 mt-4 max-w-xl text-sm md:text-base">Shodan Ready Kits combine practical gear into complete solutions for real-world situations. Choose your mission and your readiness level — Basic, Complete or Advanced.</p>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 space-y-16">
        {families.map((f) => (
          <section key={f.key} data-testid={`kit-family-${f.key}`}>
            <div className="flex items-end justify-between mb-6">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl tracking-tight uppercase font-bold">{f.name}</h2>
                <p className="text-black/50 text-xs mt-1">Choose your readiness level</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-4 pt-3">
              {f.kits.map((kit) => <KitCard key={kit.slug} kit={kit} />)}
            </div>
          </section>
        ))}
        <section className="border border-black/10 bg-[#F4F3F0] p-8 md:p-12 text-center">
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight uppercase font-bold">Build Your Own</h2>
          <p className="text-black/55 text-sm mt-2 max-w-lg mx-auto">Want something different? Start with a Ready Kit and customize it — or build your own setup from scratch.</p>
          <Link data-testid="build-your-own-btn" to="/shop" onClick={() => track("kit_click", "kits_page", { kit: "build_your_own" })}
            className="inline-flex items-center gap-2 mt-6 bg-[#121212] text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150">
            Build My Own Kit <ArrowRight size={15} />
          </Link>
        </section>
      </div>
    </div>
  );
}
